import {useEffect, useState} from "react";
import Modal from "patient-ping-remedy/packages/modal";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Input from "patient-ping-remedy/packages/input";
import ValidatedInput from "@common/ValidatedInput";
import {
  SmallSemiBoldTypography,
  StandardSemiBoldTypography,
} from "patient-ping-remedy/packages/typography";
import {
  ModalContent,
  ModalRow,
  PreviewBlock,
  PreviewText,
  TemplateContent
} from "@/components/pages/settings/telephony/VoicemailConfigsForm.styles";
import VoicemailTemplateField from "@/components/pages/settings/telephony/VoicemailTemplateField";
import {
  FALLBACK_VARIABLES,
  FOLLOW_UP_FALLBACK_PLACEHOLDER,
  FOLLOW_UP_PROVIDER_KNOWN_PLACEHOLDER,
  INITIAL_FALLBACK_PLACEHOLDER,
  INITIAL_PROVIDER_KNOWN_PLACEHOLDER,
  PREVIEW_SAMPLE,
  PROVIDER_KNOWN_VARIABLES
} from "@/components/pages/settings/telephony/VoicemailConfigsForm.constants";
import {
  resolveVoicemailTemplate,
  VoicemailTemplateVariable,
  VoicemailTemplateVariables
} from "@/helpers/voicemail_template_helpers";
import {parseVoicemailAttempt, parseVoicemailOnAttemptsAsString} from "@/helpers/voicemail_attempts_helpers";
import {css} from "@emotion/css";
import {VoicemailConfigRequest, VoicemailConfigResponse} from "@/api/dto/connect";
import DisplayHelpers from "@/helpers/display_helpers";
import {VoicemailConfigChangeRequest} from "@/components/pages/settings/telephony/VoiceMailConfigsTab";

type Props = {
    isOpen: boolean;
    close: () => void;
    onSave: (request: VoicemailConfigChangeRequest) => void;
    editTarget: VoicemailConfigResponse | null;
    isSaving: boolean;
}

const validateCallerPersonaName = (value: string): string =>
  /\d/.test(value) ? 'Caller persona name cannot contain numbers.' : '';

export default function VoicemailConfigsForm({isOpen, close, onSave, editTarget, isSaving}: Props) {
  const [callerPersonaName, setCallerPersonaName] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [initialProviderKnown, setInitialProviderKnown] = useState('');
  const [initialFallback, setInitialFallback] = useState('');
  const [followUpProviderKnown, setFollowUpProviderKnown] = useState('');
  const [followUpFallback, setFollowUpFallback] = useState('');
  const [voicemailConfigName, setVoicemailConfigName] = useState('');
  const [voicemailOnAttemptsInput, setVoicemailOnAttemptsInput] = useState('');
  const [attemptsTouched, setAttemptsTouched] = useState(false);
  const [followupAfterAttemptInput, setFollowupAfterAttemptInput] = useState('');
  const [followupAfterAttemptTouched, setFollowupAfterAttemptTouched] = useState(false);

  const {attempts, error: attemptsError} = parseVoicemailOnAttemptsAsString(voicemailOnAttemptsInput);
  const showAttemptsError = attemptsTouched && attemptsError !== null;

  // The persona is a human caller name (e.g. "Laura"); digits are not valid.
  // Empty string means valid, matching the ValidatedInput contract.
  const callerPersonaNameError = validateCallerPersonaName(callerPersonaName);

  const isEdit = !!editTarget;

  const {attempt: followupAfterAttempt, error: followupAfterAttemptError} =
    parseVoicemailAttempt(followupAfterAttemptInput);
  const showFollowupAfterAttemptError = followupAfterAttemptTouched && followupAfterAttemptError !== null;

  const previewVariables: VoicemailTemplateVariables = {
    ...PREVIEW_SAMPLE,
    [VoicemailTemplateVariable.CALLER_NAME]: callerPersonaName,
    [VoicemailTemplateVariable.CALLBACK_NUMBER]: callbackNumber,
    [VoicemailTemplateVariable.PRACTICE_NAME]: practiceName,
  };

  useEffect(() => {
    if (!isOpen) return;
    setCallerPersonaName(editTarget?.callerPersonaName ?? '');
    setCallbackNumber(editTarget?.callbackNumber ?? '');
    setPracticeName(editTarget?.practiceName ?? '');
    setInitialProviderKnown(editTarget?.voicemailTemplate ?? '');
    setInitialFallback(editTarget?.voicemailTemplateFallback ?? '');
    setFollowUpProviderKnown(editTarget?.voicemailTemplateFollowup ?? '');
    setFollowUpFallback(editTarget?.voicemailTemplateFollowupFallback ?? '');
    setVoicemailConfigName(editTarget?.name ?? '');
    setVoicemailOnAttemptsInput(editTarget ? editTarget.voicemailOnAttempts.join(", ") : '');
    setFollowupAfterAttemptInput(editTarget ? editTarget.followupAfterAttempt.toString() : '');
    // Clear transient validation flags so a reopened modal is not pre-marked as errored.
    setAttemptsTouched(false);
    setFollowupAfterAttemptTouched(false);
  }, [isOpen, editTarget]);

  const trimmedName = voicemailConfigName.trim();
  const trimmedCallerPersonaName = callerPersonaName.trim();
  const trimmedPracticeName = practiceName.trim();
  const trimmedInitialProviderKnown = initialProviderKnown.trim();
  const trimmedInitialFallback = initialFallback.trim();

  const allRequiredFieldsFilled =
    trimmedName.length > 0 &&
    trimmedCallerPersonaName.length > 0 &&
    callbackNumber.length > 0 &&
    trimmedPracticeName.length > 0 &&
    attempts.length > 0 &&
    followupAfterAttempt !== null &&
    trimmedInitialProviderKnown.length > 0 &&
    trimmedInitialFallback.length > 0;

  const isChanged = isEdit
    ? trimmedName !== editTarget!.name ||
      trimmedCallerPersonaName !== editTarget!.callerPersonaName ||
      callbackNumber !== editTarget!.callbackNumber ||
      trimmedPracticeName !== editTarget!.practiceName ||
      voicemailOnAttemptsInput !== editTarget!.voicemailOnAttempts.join(", ") ||
      followupAfterAttempt !== editTarget!.followupAfterAttempt ||
      initialProviderKnown !== editTarget!.voicemailTemplate ||
      initialFallback !== editTarget!.voicemailTemplateFallback ||
      followUpProviderKnown !== editTarget!.voicemailTemplateFollowup ||
      followUpFallback !== editTarget!.voicemailTemplateFollowupFallback
    : true;

  const canSubmit =
    !attemptsError && !followupAfterAttemptError && !callerPersonaNameError &&
    allRequiredFieldsFilled && isChanged;

  const handleSave = () => {
    if (attemptsError || followupAfterAttemptError) {
      setAttemptsTouched(true);
      setFollowupAfterAttemptTouched(true);
      return;
    }
    const request: VoicemailConfigRequest = {
      name: voicemailConfigName,
      voicemailOnAttempts: attempts,
      voicemailTemplate: initialProviderKnown,
      voicemailTemplateFallback: initialFallback,
      voicemailTemplateFollowup: followUpProviderKnown,
      voicemailTemplateFollowupFallback: followUpFallback,
      practiceName: practiceName,
      callbackNumber: callbackNumber,
      callerPersonaName: callerPersonaName,
      followupAfterAttempt: followupAfterAttempt!};

    if (!isEdit) {
      onSave({mode: 'create', request: request});
    } else {
      onSave({mode: 'edit', id: editTarget?.id, request: request});
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        hideModal={close}
        headerText={isEdit ? 'Edit Voicemail Config' : 'Add Voicemail Config'}
        className={css({ width: '70rem' })}
        buttons={[
          <Button
            key={'cancel'}
            styleType={StyleType.TERTIARY}
            onClick={close}
          >
              Cancel
          </Button>,
          <Button
            key={'save'}
            onClick={handleSave}
            disabled={!canSubmit || isSaving}
          >
              Save
          </Button>
        ]}
      >
        <ModalContent>
          <Input
            id='voicemail-config-name'
            labelText='Config Name'
            placeholder='Tufts ADT Voicemail'
            required='required'
            value={voicemailConfigName}
            onChange={(event) => setVoicemailConfigName(event.target.value)}
          />
          <ModalRow>
            <ValidatedInput
              id='voicemail-caller-persona-name'
              labelText='Caller Persona Name'
              placeholder='Laura'
              required='required'
              value={callerPersonaName}
              validate={validateCallerPersonaName}
              onChange={(event) => setCallerPersonaName(event.target.value)}
            />
            <Input
              id='voicemail-callback-number'
              labelText='Callback Number'
              placeholder='(617) 209-2376'
              required='required'
              value={DisplayHelpers.formatPhoneNumber(callbackNumber)}
              onChange={(event) => {
                const raw = event.target.value.replace(/\D/g, '').substring(0, 10);
                setCallbackNumber(raw);
              }
              }
            />
          </ModalRow>
          <Input
            id='voicemail-practice-name'
            labelText='Practice Name'
            placeholder='Tufts'
            required='required'
            value={practiceName}
            onChange={(event) => setPracticeName(event.target.value)}
          />
          <Input
            id='voicemail-on-attempts'
            labelText='Voicemail on Attempts'
            placeholder='1, 2, 3, 5, 7, 9'
            required='required'
            value={voicemailOnAttemptsInput}
            error={showAttemptsError}
            errorMessage={showAttemptsError ? attemptsError! : ''}
            onChange={(event) => setVoicemailOnAttemptsInput(event.target.value)}
            onBlur={() => setAttemptsTouched(true)}
          />
          <Input
            id='followup-after-attempt'
            labelText='Switch to Followup Templates After Attempt'
            placeholder='3'
            required='required'
            type='number'
            inputMode='numeric'
            value={followupAfterAttemptInput}
            error={showFollowupAfterAttemptError}
            errorMessage={showFollowupAfterAttemptError ? followupAfterAttemptError! : ''}
            onChange={(event) => setFollowupAfterAttemptInput(event.target.value)}
            onBlur={() => setFollowupAfterAttemptTouched(true)}
            className={css({'input': {width: '8rem'}})}
          />
          <TemplateContent>
            <StandardSemiBoldTypography>Templates</StandardSemiBoldTypography>

            <VoicemailTemplateField
              id='voicemail-template-initial-provider-known'
              labelText='Initial - Provider Known'
              value={initialProviderKnown}
              placeholder={INITIAL_PROVIDER_KNOWN_PLACEHOLDER}
              supportedVariables={PROVIDER_KNOWN_VARIABLES}
              required
              onChange={setInitialProviderKnown}
            />
            <PreviewBlock>
              <SmallSemiBoldTypography>Preview of 'Initial - Provider Known'</SmallSemiBoldTypography>
              <PreviewText>
                {resolveVoicemailTemplate(initialProviderKnown, previewVariables)}
              </PreviewText>
            </PreviewBlock>

            <VoicemailTemplateField
              id='voicemail-template-initial-fallback'
              labelText='Initial - No Provider / fallback'
              value={initialFallback}
              placeholder={INITIAL_FALLBACK_PLACEHOLDER}
              supportedVariables={FALLBACK_VARIABLES}
              required
              onChange={setInitialFallback}
            />
            <PreviewBlock>
              <SmallSemiBoldTypography>Preview of 'Initial - No Provider / fallback'</SmallSemiBoldTypography>
              <PreviewText>
                {resolveVoicemailTemplate(initialFallback, previewVariables)}
              </PreviewText>
            </PreviewBlock>

            <VoicemailTemplateField
              id='voicemail-template-follow-up-provider-known'
              labelText='Follow-up - Provider Known'
              value={followUpProviderKnown}
              placeholder={FOLLOW_UP_PROVIDER_KNOWN_PLACEHOLDER}
              supportedVariables={PROVIDER_KNOWN_VARIABLES}
              onChange={setFollowUpProviderKnown}
            />
            <PreviewBlock>
              <SmallSemiBoldTypography>Preview of 'Follow-up - Provider Known'</SmallSemiBoldTypography>
              <PreviewText>
                {resolveVoicemailTemplate(followUpProviderKnown, previewVariables)}
              </PreviewText>
            </PreviewBlock>

            <VoicemailTemplateField
              id='voicemail-template-follow-up-fallback'
              labelText='Follow-up - No Provider / fallback'
              value={followUpFallback}
              placeholder={FOLLOW_UP_FALLBACK_PLACEHOLDER}
              supportedVariables={FALLBACK_VARIABLES}
              onChange={setFollowUpFallback}
            />
            <PreviewBlock>
              <SmallSemiBoldTypography>Preview of 'Follow-up - No Provider / fallback'</SmallSemiBoldTypography>
              <PreviewText>
                {resolveVoicemailTemplate(followUpFallback, previewVariables)}
              </PreviewText>
            </PreviewBlock>
          </TemplateContent>
        </ModalContent>
      </Modal>
    </>
  );
}
